import type { ReactNode } from 'react'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { Box, Button, Link, Typography } from '@mui/material'
import { useOnboarding } from '../context/OnboardingContext'
import { OnboardingPhaseProgress } from './OnboardingPhaseProgress'
import {
  clearClientOnboardingComplete,
  markClientOnboardingComplete,
} from '../lib/onboardingPortalFlags'
import { AppRoutes } from '../../helper/appRoutes'
import { MISC_PRIMARY, MISC_PRIMARY_HOVER } from '../theme/onboardingTheme'

export function WizardChrome({ children }: { children: ReactNode }) {
  const navigate = useNavigate()
  const {
    currentStepId,
    canAdvance,
    advance,
    back,
    stepIndex,
    dispatch,
    stepBackOverride,
  } = useOnboarding()

  const saveAndExit = () => {
    if (currentStepId === 'complete') {
      markClientOnboardingComplete()
    }
    dispatch({ type: 'TOAST', message: 'Progress saved (demo). Returning to portal.' })
    navigate(AppRoutes.client.home)
  }

  const wideLayout =
    currentStepId === 'generalQuestions' ||
    currentStepId === 'businessLicenses' ||
    currentStepId === 'individualLicenses' ||
    currentStepId === 'drlpAssignment' ||
    currentStepId === 'contacts' ||
    currentStepId === 'logins' ||
    currentStepId === 'separateMiscAccountGuide' ||
    currentStepId === 'newLoginWizard' ||
    currentStepId === 'idahoAgreement' ||
    currentStepId === 'texasAdminAccountAccess' ||
    currentStepId === 'oregonNorthCarolinaUserAccountAccess' ||
    currentStepId === 'newYorkAdminAccountAccess' ||
    currentStepId === 'floridaGeorgiaMississippiUserAccountAccess' ||
    currentStepId === 'maineTaxPortalAccess' ||
    currentStepId === 'rhodeIslandTaxPortalAccess' ||
    currentStepId === 'vermontMyVaxAccess' ||
    currentStepId === 'newMexicoTapAccess' ||
    currentStepId === 'otr'

  const contentWidthSx = wideLayout
    ? { width: '100%', maxWidth: 'none' }
    : { mx: 'auto', maxWidth: 768 }

  return (
    <Box sx={{ display: 'flex', minHeight: 0, flex: 1, flexDirection: 'column' }}>
      <Box
        sx={{
          borderBottom: '1px solid #e2e8f0',
          backgroundColor: '#ffffff',
          px: { xs: 2, lg: 5 },
          py: 2,
        }}
      >
        <Box sx={contentWidthSx}>
          <Typography
            sx={{
              fontSize: 12,
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: '0.04em',
              color: '#64748b',
            }}
          >
            Onboarding
          </Typography>
          <Box
            sx={{
              mt: 0.5,
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'flex-end',
              justifyContent: 'space-between',
              gap: 1,
            }}
          >
            <Typography sx={{ fontSize: '1.25rem', fontWeight: 600, color: MISC_PRIMARY }}>
              Welcome to MISC! Please complete your onboarding
            </Typography>
            <Button
              type="button"
              onClick={() => {
                clearClientOnboardingComplete()
                dispatch({ type: 'RESET' })
              }}
              sx={{
                minWidth: 0,
                p: 0,
                fontSize: 12,
                fontWeight: 600,
                color: '#64748b',
                '&:hover': { color: MISC_PRIMARY, textDecoration: 'underline', backgroundColor: 'transparent' },
              }}
            >
              Reset demo
            </Button>
          </Box>
          <Typography sx={{ mt: 0.5, fontSize: 14, color: '#475569' }}>
            This wizard will help guide you through the onboarding process. If you have questions at
            any time, please email{' '}
            <Link href="mailto:onboarding@morrisisc.com" sx={{ fontWeight: 500, color: MISC_PRIMARY }}>
              onboarding@morrisisc.com
            </Link>
            .
          </Typography>
          <OnboardingPhaseProgress currentStepId={currentStepId} />
        </Box>
      </Box>

      <Box
        sx={{
          flex: 1,
          overflowY: 'auto',
          px: { xs: 2, lg: 5 },
          py: 4,
          pb: wideLayout ? { xs: 12, lg: 14 } : 12,
        }}
      >
        <Box sx={contentWidthSx}>{children}</Box>
      </Box>

      <Box
        component="footer"
        sx={{
          position: 'sticky',
          bottom: 0,
          borderTop: '1px solid #e2e8f0',
          backgroundColor: 'rgba(255,255,255,0.95)',
          backdropFilter: 'blur(8px)',
          px: { xs: 2, lg: 5 },
          py: 2,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1.5,
            ...contentWidthSx,
          }}
        >
          <Box sx={{ display: 'flex', minWidth: 0, flexWrap: 'wrap', alignItems: 'center', gap: 1 }}>
            <Button
              type="button"
              variant="outlined"
              onClick={() => {
                if (stepBackOverride?.()) return
                back()
              }}
              disabled={stepIndex <= 0}
              startIcon={<ChevronLeft size={16} />}
              sx={{
                borderColor: '#cbd5e1',
                color: '#334155',
                px: 2,
                py: 1.25,
                '&:hover': { backgroundColor: '#f8fafc' },
                '&.Mui-disabled': { opacity: 0.4 },
              }}
            >
              Back
            </Button>
            <Button
              type="button"
              variant="outlined"
              onClick={saveAndExit}
              sx={{
                borderColor: 'rgba(37, 99, 235, 0.5)',
                backgroundColor: '#eff6ff',
                color: MISC_PRIMARY,
                fontWeight: 600,
                px: 2,
                py: 1.25,
                '&:hover': { backgroundColor: 'rgba(219, 234, 254, 0.8)' },
              }}
            >
              Save &amp; Exit
            </Button>
          </Box>
          {currentStepId !== 'complete' ? (
            <Button
              type="button"
              variant="contained"
              onClick={advance}
              disabled={!canAdvance}
              endIcon={<ChevronRight size={16} />}
              sx={{
                flexShrink: 0,
                backgroundColor: MISC_PRIMARY,
                px: 2.5,
                py: 1.25,
                boxShadow: 1,
                '&:hover': { backgroundColor: MISC_PRIMARY_HOVER },
                '&.Mui-disabled': { opacity: 0.4 },
              }}
            >
              Continue
            </Button>
          ) : null}
        </Box>
      </Box>
    </Box>
  )
}
